export const OPERATION_POLL_INTERVAL_MS = 5000;
export const MAX_CONSECUTIVE_STATUS_FAILURES = 5;
export const STATUS_UNAVAILABLE_MESSAGE = (
  'Status is not available now. The process may still be running.'
);

const currentDocument = () => (
  typeof document === 'undefined' ? null : document
);

const waitUntilVisible = (signal) => new Promise((resolve) => {
  if (signal?.aborted) {
    resolve(false);
    return;
  }

  const pageDocument = currentDocument();
  if (!pageDocument?.hidden) {
    resolve(true);
    return;
  }

  const finish = (shouldContinue) => {
    pageDocument.removeEventListener('visibilitychange', handleVisibilityChange);
    signal?.removeEventListener('abort', handleAbort);
    resolve(shouldContinue);
  };
  const handleAbort = () => finish(false);
  const handleVisibilityChange = () => {
    if (!pageDocument.hidden) finish(true);
  };

  pageDocument.addEventListener('visibilitychange', handleVisibilityChange);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

const waitForNextPoll = (intervalMs, signal) => new Promise((resolve) => {
  if (signal?.aborted) {
    resolve(false);
    return;
  }

  const pageDocument = currentDocument();
  let timeoutId = null;
  let paused = Boolean(pageDocument?.hidden);

  const finish = (shouldContinue) => {
    if (timeoutId !== null) clearTimeout(timeoutId);
    pageDocument?.removeEventListener('visibilitychange', handleVisibilityChange);
    signal?.removeEventListener('abort', handleAbort);
    resolve(shouldContinue);
  };
  const handleAbort = () => finish(false);
  const handleVisibilityChange = () => {
    if (pageDocument.hidden) {
      paused = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      return;
    }
    if (paused) finish(true);
  };

  if (!paused) timeoutId = setTimeout(() => finish(true), intervalMs);
  pageDocument?.addEventListener('visibilitychange', handleVisibilityChange);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

export function createOperationMonitoringError({
  cause,
  code,
  operationId,
  operation,
  message = STATUS_UNAVAILABLE_MESSAGE,
  context = {},
}) {
  const error = new Error(message);
  error.code = code;
  error.isMonitoringError = true;
  error.operationId = operationId;
  error.operation = operation || null;
  error.cause = cause;
  Object.assign(error, context);
  return error;
}

/**
 * Poll one already-known background operation. The first GET is immediate while
 * the page is visible; later GETs use one fixed interval. Unknown statuses are
 * treated as status-read failures, never as a fabricated domain outcome.
 */
export default async function pollKnownOperationStatus({
  operationId,
  initialOperation = null,
  initialStatus = initialOperation?.status,
  fetchStatus,
  getStatus = (operation) => operation?.status,
  isActiveStatus,
  isTerminalStatus,
  onStatus = () => {},
  signal,
  intervalMs = OPERATION_POLL_INTERVAL_MS,
  maxConsecutiveFailures = MAX_CONSECUTIVE_STATUS_FAILURES,
  monitoringErrorCode = 'STATUS_UNAVAILABLE',
  monitoringErrorContext = {},
}) {
  if (!operationId || signal?.aborted || !isActiveStatus(initialStatus)) return null;

  let consecutiveFailures = 0;
  let lastOperation = initialOperation;
  if (!await waitUntilVisible(signal)) return null;

  while (!signal?.aborted) {
    try {
      const operation = await fetchStatus({ signal });
      if (signal?.aborted) return null;

      const status = getStatus(operation);
      if (!isActiveStatus(status) && !isTerminalStatus(status)) {
        throw new Error('Invalid operation status response.');
      }

      consecutiveFailures = 0;
      lastOperation = operation;
      onStatus(operation);
      if (isTerminalStatus(status)) return operation;
    } catch (error) {
      if (signal?.aborted) return null;
      consecutiveFailures += 1;
      if (consecutiveFailures >= maxConsecutiveFailures) {
        throw createOperationMonitoringError({
          cause: error,
          code: monitoringErrorCode,
          operationId,
          operation: lastOperation,
          context: monitoringErrorContext,
        });
      }
    }

    if (!await waitForNextPoll(intervalMs, signal)) return null;
  }
  return null;
}
